import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DbChallengeSolution, SOLUTION_TYPES } from '@/hooks/useChallengeLab';
import { Folder, Plus, CheckCircle2, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface SolutionsListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  solutions: DbChallengeSolution[];
  challengeTitle: string;
  onAddSolution: () => void;
  onViewSolution: (solution: DbChallengeSolution) => void;
}

export function SolutionsListModal({ open, onOpenChange, solutions, challengeTitle, onAddSolution, onViewSolution }: SolutionsListModalProps) {
  const getSolutionTypeInfo = (typeId: string | null) => {
    return SOLUTION_TYPES.find(t => t.id === typeId) || { name: typeId || 'General', icon: '📋' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-primary flex items-center justify-center shrink-0">
                <Folder className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-lg">Proyectos Prospecto</DialogTitle>
                <p className="text-sm text-muted-foreground truncate mt-0.5">{challengeTitle}</p>
              </div>
            </div>
            <Button size="sm" onClick={onAddSolution} className="shrink-0 gap-1">
              <Plus className="w-4 h-4" />Agregar
            </Button>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] mt-4">
          {solutions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Folder className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium">No hay soluciones identificadas</p>
              <p className="text-xs mt-1">Agrega proyectos prospecto para este reto</p>
              <Button size="sm" className="mt-4 gap-1" onClick={onAddSolution}>
                <Plus className="w-4 h-4" />Identificar Solución
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {solutions.map((solution) => {
                const typeInfo = getSolutionTypeInfo(solution.solution_type);
                return (
                  <div key={solution.id} className="p-3 rounded-lg border border-border/50 hover:border-primary/30 bg-card transition-colors cursor-pointer group" onClick={() => onViewSolution(solution)}>
                    <div className="flex items-start gap-3">
                      <span className="text-xl">{typeInfo.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium truncate">{solution.name}</h4>
                          {solution.converted_to_project && (
                            <Badge variant="default" className="text-[10px] px-1.5 py-0 gap-0.5">
                              <CheckCircle2 className="w-3 h-3" />Proyecto
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{typeInfo.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{solution.description}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-muted-foreground">
                            {format(new Date(solution.created_at), "d MMM yyyy", { locale: es })}
                          </span>
                          <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                            Ver detalle <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
